import { Box, Card, Flex, Text } from '@mantine/core';
import { GetMonthlyEnergyStatsResponse } from 'api/actions/get-monthly-energy-stats/get-monthly-energy-stats-response';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface MonthlySavingsChartCardProps {
  /** Monthly energy statistics including daily savings breakdown */
  monthlyStats: GetMonthlyEnergyStatsResponse;
}

/**
 * Displays a bar chart of daily savings/overpayments for the selected month.
 * Green bars = savings (spot cheaper), red bars = overpayments (spot more expensive).
 *
 * @prop {GetMonthlyEnergyStatsResponse} monthlyStats - Monthly stats with dailySavings array.
 */
export default function MonthlySavingsChartCard({ monthlyStats }: MonthlySavingsChartCardProps) {
  const { _t } = useLang();
  const { dailySavings, month, fixedTariff } = monthlyStats;

  const chartData = useMemo(
    () => ({
      labels: dailySavings.map((day) => {
        const d = new Date(day.date!);
        return `${d.getDate()}.${d.getMonth() + 1}.`;
      }),
      datasets: [
        {
          label: _t('Savings/overpayment (€)'),
          data: dailySavings.map((day) => day.savings || 0),
          backgroundColor: dailySavings.map((day) => ((day.savings || 0) >= 0 ? '#10B981' : '#EF4444')),
          hoverBackgroundColor: dailySavings.map((day) => ((day.savings || 0) >= 0 ? '#059669' : '#DC2626')),
          borderRadius: 5,
          borderSkipped: false as const,
          barPercentage: 0.9,
        },
      ],
    }),
    [_t, dailySavings]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: {
            color: theme.colors?.gray?.[7],
            callback: (v: number | string) => `${Number(v).toFixed(4)} €`,
          },
          grid: yGridConfig,
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = ctx.parsed.y;
              return `${v >= 0 ? _t('Savings') : _t('Overpayment')}: ${Math.abs(v).toFixed(4)} €`;
            },
          },
        },
      },
    }),
    [_t]
  );

  const totalSavings = dailySavings.reduce((sum, day) => sum + (day.savings || 0), 0);

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Daily savings/overpayments for the month')} - {month}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total monthly savings/overpayment')}:
          </Text>
          <Text size="xl" fw={700} c={totalSavings >= 0 ? '#10B981' : '#EF4444'}>
            {totalSavings.toFixed(4)} €
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          {_t('Fixed tariff')}: {fixedTariff.toFixed(2)} €/MWh
        </Text>
      </Box>
    </Card>
  );
}
